// The requester step: every person at the chosen organization, listed immediately.
//
// SPEC.md §6 is explicit that the list appears on selection rather than after more
// typing — the list is short, and picking beats typing a name the agent is hearing
// for the first time and cannot spell. The filter box narrows what is already
// loaded, so it costs no request and no wait.

import { useMemo, useState } from 'react';

import useListNavigation from './useListNavigation.js';
import ResultList from './ResultList.jsx';

function matches(person, needle) {
  const digits = needle.replace(/\D/g, '');
  const haystack = [person.name, person.email, person.company, person.location_name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (haystack.includes(needle)) return true;
  // Agents search by the number the caller reads out, in whatever format they read
  // it — the same digits-only rule the API applies (docs/API.md §4.4).
  return digits.length >= 3 && String(person.phone ?? '').replace(/\D/g, '').includes(digits);
}

export default function RequesterList({
  people,
  status,
  error,
  filterRef,
  selectedLocationId,
  onSelect,
  onCreateNew,
}) {
  const [filter, setFilter] = useState('');

  const visible = useMemo(() => {
    const rows = people ?? [];
    const needle = filter.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((person) => matches(person, needle));
  }, [people, filter]);

  const { activeIndex, setActiveIndex, handleKeyDown } = useListNavigation(visible, {
    onSelect,
  });

  return (
    <div className="mt-3">
      <label className="field-label" htmlFor="requester-filter">
        Requester
      </label>
      <input
        id="requester-filter"
        ref={filterRef}
        className="field-input"
        role="combobox"
        aria-expanded={visible.length > 0}
        aria-controls="requester-listbox"
        aria-autocomplete="list"
        aria-activedescendant={visible.length ? `requester-option-${activeIndex}` : undefined}
        autoComplete="off"
        placeholder={status === 'loading' ? 'Loading people…' : 'Filter by name, email or phone'}
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        onKeyDown={handleKeyDown}
      />

      {status === 'error' && (
        <p className="mt-2 text-sm text-red-700 dark:text-red-300">
          {error?.message ?? 'The requester list could not be loaded.'}
        </p>
      )}

      {visible.length > 0 && (
        <ResultList
          idPrefix="requester"
          label="People at this organization"
          items={visible}
          activeIndex={activeIndex}
          onHover={setActiveIndex}
          onSelect={onSelect}
          getKey={(person) => person.id}
          renderItem={(person) => (
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-medium">{person.name ?? person.email}</span>
              <span className="truncate text-xs text-ink-muted group-data-[active=true]:text-brand-50">
                {[person.location_name, person.email].filter(Boolean).join(' · ')}
                {person.location_id && person.location_id === selectedLocationId ? ' ✓' : ''}
              </span>
            </div>
          )}
        />
      )}

      {status === 'ready' && visible.length === 0 && (
        <p className="mt-2 text-sm text-ink-muted">
          {people?.length
            ? 'Nobody here matches that.'
            : 'This organization has no contacts on file yet.'}
        </p>
      )}

      <button
        type="button"
        className="btn-ghost mt-2 px-0 text-brand-700 dark:text-brand-300"
        onClick={onCreateNew}
      >
        + New contact <span className="text-xs text-ink-muted">(Alt+N)</span>
      </button>
    </div>
  );
}
